/**
 * Multi-Provider Analytics Hook
 *
 * Routes creator events to both providers and AUD_ events only to PostHog.
 * PostHog: via React hook (posthog-js/react)
 * Customer.io: via track.js snippet (standard JS integration)
 */

"use client";

import { useCallback, useEffect, useMemo } from "react";
import { usePostHog as usePH } from "posthog-js/react";
import type { FrontendEvent, EventProperties, EngagementTier } from "@artha/types";
import { AnalyticsManager } from "./manager";
import { getAnalyticsConfig } from "./config";
import type { PostHogProvider } from "./providers/posthog";

interface CreatorIdentity {
  id: string;
  email?: string;
  username: string;
  displayName?: string;
  nicheCategories?: string[];
  primaryPlatform?: string;
  primarySocialAccount?: string;
  primarySocialUrl?: string;
  name: string;
  totalFollowers?: number;
  enrichmentStatus?: string;
}

// Create a singleton instance of the AnalyticsManager
let analyticsManagerInstance: AnalyticsManager | null = null;

function getAnalyticsManagerInstance(): AnalyticsManager {
  if (!analyticsManagerInstance) {
    analyticsManagerInstance = new AnalyticsManager(getAnalyticsConfig());
    // Initializing doesn't wait for provider scripts (they handle their own loading)
    analyticsManagerInstance.initialize().catch(err =>
      console.error("[Analytics] Failed to initialize manager:", err)
    );
  }
  return analyticsManagerInstance;
}

export function useAnalytics() {
  const posthog = usePH();
  const manager = useMemo(() => getAnalyticsManagerInstance(), []);

  // Sync PostHog instance from React hook to the manager's provider
  useEffect(() => {
    if (posthog) {
      const phProvider = manager.getProvider<PostHogProvider>("posthog");
      phProvider?.setPostHogInstance(posthog);
    }
  }, [posthog, manager]);

  const identifyCreator = useCallback(
    async (creator: CreatorIdentity) => {
      const tier = getEngagementTier(creator.totalFollowers || 0);

      const traits: Record<string, unknown> = {
        email: creator.email,
        username: creator.username,
        name: creator.displayName,
        displayName: creator.displayName,
        nicheCategories: creator.nicheCategories,
        primaryPlatform: creator.primaryPlatform,
        primary_social_account: creator.primaryPlatform,
        primary_social_url: creator.primarySocialUrl,
        followers: Number(creator.totalFollowers || 0),
        totalFollowers: Number(creator.totalFollowers || 0),
        enrichmentStatus: creator.enrichmentStatus,
        engagement_tier: tier,
        primary_niche: creator.nicheCategories?.[0],
      };

      await manager.identify(creator.id, traits);
    },
    [manager]
  );

  const trackEvent = useCallback(
    (eventName: FrontendEvent | string, properties?: EventProperties) => {
      manager.track(eventName, properties as Record<string, unknown>);
    },
    [manager]
  );

  const updateUserProperties = useCallback(
    (userId: string, traits: Record<string, unknown>) => {
      manager.identify(userId, traits);
    },
    [manager]
  );

  const trackPage = useCallback(
    (pageName?: string, properties?: EventProperties) => {
      manager.page(pageName, properties as Record<string, unknown>);
    },
    [manager]
  );

  const reset = useCallback(async () => {
    await manager.reset();
  }, [manager]);

  const isFeatureEnabled = useCallback(
    (flag: string): boolean => {
      const phProvider = manager.getProvider<PostHogProvider>("posthog");
      return phProvider?.isFeatureEnabled(flag) ?? false;
    },
    [manager]
  );

  const getFeatureFlag = useCallback(
    (flag: string): string | boolean | undefined => {
      const phProvider = manager.getProvider<PostHogProvider>("posthog");
      return phProvider?.getFeatureFlag(flag);
    },
    [manager]
  );

  const getAnalyticsManager = useCallback(() => manager, [manager]);
  const getAnalyticsStats = useCallback(() => manager.getStats(), [manager]);

  return {
    identifyCreator,
    trackEvent,
    updateUserProperties,
    trackPage,
    reset,
    isFeatureEnabled,
    getFeatureFlag,
    getAnalyticsManager,
    getAnalyticsStats,
    posthog,
  };
}

/**
 * @deprecated Use useAnalytics instead
 */
export function usePostHog() {
  return useAnalytics();
}

function getEngagementTier(followers: number): EngagementTier {
  if (followers >= 100000) return "mega";
  if (followers >= 10000) return "macro";
  if (followers >= 1000) return "micro";
  return "nano";
}
